import React, { useState, useEffect, useRef } from 'react';
import CategoryCard from '../../../../components/card/CategoryCard';
import './ShopByCategory.css';

const ShopByCategory = ({ categories, hasWhiteBg = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const categoryListRef = useRef(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      checkIsMobile();
    };

    checkIsMobile();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get card width based on screen size (using original grid values)
  const getCardWidth = () => {
    if (windowWidth <= 480) return 350 + 8; // original grid gap
    if (windowWidth <= 768) return 350 + 16; // original grid gap
    if (windowWidth <= 1024) return 350 + 20; // original grid gap
    return 350 + 24; // original grid gap
  };

  // Get max categories based on screen size
  const getMaxCategories = () => {
    if (windowWidth <= 768) return 2; // mobile
    if (windowWidth > 2042) return 5; // large desktop
    return 3; // regular desktop
  };

  // Get CSS class for scroller based on screen size
  const getScrollerClass = () => {
    if (windowWidth <= 768) return 'home-shop-category__scroller--mobile';
    if (windowWidth > 2042) return 'home-shop-category__scroller--large-desktop';
    return 'home-shop-category__scroller--desktop';
  };

  // Update current index when user scrolls manually
  useEffect(() => {
    const scroller = categoryListRef.current;
    if (!scroller) return;

    let isScrolling = false;
    const handleScroll = () => {
      if (isScrolling) return; // Prevent interference with auto-scroll
      isScrolling = true;
      
      const cardWidth = getCardWidth();
      const scrollIndex = Math.round(scroller.scrollLeft / cardWidth);
      // Ensure the index is within bounds
      const currentIndex = Math.max(0, Math.min(scrollIndex, categories.length - 1));
      setCurrentCategoryIndex(currentIndex);
      
      setTimeout(() => { isScrolling = false; }, 100);
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [windowWidth, categories.length]);

  // Auto-advance categories by groups (3 on desktop, 2 on mobile)
  useEffect(() => {
    if (!categoryListRef.current || categories.length === 0) return;
    const scroller = categoryListRef.current;
    const intervalId = setInterval(() => {
      const cardWidth = getCardWidth();
      const maxCategories = getMaxCategories();
      
      // Calculate next group start index based on current state
      let nextIndex;
      if (currentCategoryIndex + maxCategories >= categories.length) {
        // If we're at the end, go back to start
        nextIndex = 0;
      } else {
        // Move to next group
        nextIndex = currentCategoryIndex + 1;
      }
      
      scroller.scrollTo({ left: nextIndex * cardWidth, behavior: "smooth" });
      setCurrentCategoryIndex(nextIndex);
    }, 4000); // Auto-advance every 4 seconds
    return () => clearInterval(intervalId);
  }, [categories, windowWidth, isMobile, currentCategoryIndex]);

  // Navigation functions - work with groups
  const goToNextCategory = () => {
    if (!categoryListRef.current || categories.length === 0) return;
    const scroller = categoryListRef.current;
    const cardWidth = getCardWidth();
    const currentIndex = Math.round(scroller.scrollLeft / cardWidth);
    const maxCategories = isMobile ? 2 : 3;
    
    let nextIndex;
    if (currentIndex + maxCategories >= categories.length) {
      nextIndex = 0; // Go back to start
    } else {
      nextIndex = currentIndex + 1; // Move to next group
    }
    
    scroller.scrollTo({ left: nextIndex * cardWidth, behavior: "smooth" });
    setCurrentCategoryIndex(nextIndex);
  };

  const goToPreviousCategory = () => {
    if (!categoryListRef.current || categories.length === 0) return;
    const scroller = categoryListRef.current;
    const cardWidth = getCardWidth();
    const currentIndex = Math.round(scroller.scrollLeft / cardWidth);
    const maxCategories = isMobile ? 2 : 3;
    
    let prevIndex;
    if (currentIndex === 0) {
      // Go to the last possible group start
      prevIndex = Math.max(0, categories.length - maxCategories);
    } else {
      prevIndex = currentIndex - 1;
    }
    
    scroller.scrollTo({ left: prevIndex * cardWidth, behavior: "smooth" });
    setCurrentCategoryIndex(prevIndex);
  };

  const goToCategory = (index) => {
    if (!categoryListRef.current || categories.length === 0) return;
    const scroller = categoryListRef.current;
    const cardWidth = getCardWidth();
    scroller.scrollTo({ left: index * cardWidth, behavior: "smooth" });
    setCurrentCategoryIndex(index);
  };

  if (categories?.length === 0) {
    return null;
  }

  // Determine how many categories to show based on screen size
  const maxCategories = getMaxCategories();
  const shouldShowScroll = categories.length > maxCategories;

  // Show original grid layout for 3 or fewer categories (desktop) or 2 or fewer (mobile)
  if (!shouldShowScroll) {
    return (
      <section className={`home-shop-category ${hasWhiteBg ? 'home-shop-category--white' : ''}`}>
        <div className="home-shop-category__header">
          <h2 className="home-shop-category__title">SHOP BY CATEGORY</h2>
        </div>
        
        <div className="home-shop-category__grid">
          {categories.map((category) => (
            <CategoryCard 
              key={category?._id} 
              category={category} 
            />
          ))}
        </div>
      </section>
    );
  }

  // Show horizontal scroll with dots for more than max categories
  return (
    <section className={`home-shop-category ${hasWhiteBg ? 'home-shop-category--white' : ''}`}>
      <div className="home-shop-category__header">
        <h2 className="home-shop-category__title">SHOP BY CATEGORY</h2>
      </div>
      
      <div className="home-shop-category__container">
        {/* Scrollable Category Container */}
        <div
          className={`home-shop-category__scroller ${getScrollerClass()}`}
          ref={categoryListRef}
        >
          {categories.map((category, index) => (
            <div key={category?._id} className="home-shop-category__item">
              <CategoryCard category={category} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShopByCategory; 